import React, { useRef, useState } from "react"
import styled from "styled-components"
import ReactEcharts from "echarts-for-react"
import moment from "moment-timezone"
import useMediaQuery from "useHooks/useMediaQuery"
import media from "cssMix/index"
import { iconOption, option } from "./chartOption"
import { IoMdClose } from "react-icons/io"
import ReactModal from "Components/common/ReactModal"

const BarChart = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  display: flex;
  .rtpIcon {
    position: relative;
    width: 100%;
    height: 100%;
    margin: auto;
    display: flex;
    justify-content: center;
    align-items: center;
    .hoverIcon {
      width: 100%;
      height: 100% !important;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.4);
      background-color: #ffffff;
      cursor: pointer;
      &:hover {
        border: 1px solid #55b0ee;
        ${media.tablet`
        border:none;`}
      }
    }
    img {
      width: 100%;
    }
  }
  .erpGraph {
    position: absolute;
    height: 400px;
    width: 700px;
    visibility: hidden;
    z-index: 5;
    top: -1000px;
    ${(props) =>
      `${props.length > 10 || props.column === 1 ? "right" : "left"}: 100%;`}
    box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    border: 1px solid #55b0ee;
    background-color: #ffffff;
    .header {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 50px;
      width: 100%;
      font-size: 18px;
      background-color: #55b0ee;
      border-radius: 8px 8px 0 0;
      color: #ffffff;
      .close {
        float: right;
        width: 30px;
        height: 30px;
        position: absolute;
        right: 12px;
        color: #ffffff;
      }
      .title {
        display: inline-block;
        font-size: 22px;
        margin-left: 15px;
      }
      img {
        margin-left: 20px;
        width: 50px;
        height: 50px;
      }
    }
    .echarts-for-react {
      background-color: #ffffff;
      height: 86% !important;
      width: 100%;
      margin: auto;
      border-radius: 0 0 8px 8px;
    }
    ${media.tablet`
      background-color: initial;
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 10;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      flex-direction: column;
      justify-content: center;
      border:none;
      .mask{
        height: 100%;
        width: 100%;
        position: absolute;
        background-color: #000;
        opacity: 0.5;
        z-index:1;
      }
      .header{
        position: relative;
        height: 50px;
        width: 80%;
        z-index:2;
      }
      .echarts-for-react {
        width: 80%;
        margin: 0;
        height: 370px !important;
        z-index:2;
      }
    `}
    ${media.mobile`
      background-color: initial;
      position: fixed;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 10;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      flex-direction: column;
      justify-content: center;
      border: 0;
      .mask{
        height: 100%;
        width: 100%;
        position: absolute;
        background-color: #000;
        opacity: 0.5;
        z-index:1;
      }
      .header{
        position: relative;
        height: 45px;
        width: 90%;
        z-index:2;
      }
      .echarts-for-react {
        width: 90%;
        margin: 0;
        height: 330px !important;
        z-index:2;
      }
    `}
  }
  &:hover {
    .erpGraph {
      visibility: visible;
      top: 0;
      margin-top: -${(props) => props.marginTop}px;
    }
    .rtpIcon {
      .hoverIcon {
        /* border: 2px solid #F8D047; */
      }
    }
  }
  ${media.tablet`
    .erpGraph{
      display: none;
      visibility: initial;
      margin-top: -${(props) => props.marginTop}px;
      &.active{
        display: flex;
        z-index:110;
      }
    }
  `}
  ${media.mobile`
    .erpGraph{
      display: none;
      visibility: initial;
      margin-top: -${(props) => props.marginTop}px;
      &.active{
        display: flex;
      }
    }
  `}
`

const BarGraph = ({
  content = [],
  gameCode,
  gameName,
  length,
  lang,
  column,
}) => {
  const $erpGraph = useRef(null)
  const $rtpIcon = useRef(null)
  const [marginTop, setMarginTop] = useState(0)
  const [showState, setShowState] = useState(false)
  const { mediaType } = useMediaQuery()
  const timezone = {
    cn: "Asia/Shanghai",
    en: "Atlantic/Bermuda",
    th: "Asia/Bangkok",
    vn: "Asia/Ho_Chi_Minh",
  }
  const mouseMove = () => {
    const customTop =
      $erpGraph.current.getBoundingClientRect().height -
      (window.innerHeight - $erpGraph.current.getBoundingClientRect().top)
    if (customTop !== 0) setMarginTop(customTop)
  }
  const rtpIconClick = () => setShowState(!showState)
  const rtpData = content.map((value) => {
    return value.get("rtpExp")
  })
  const xAxisData = content.map((value) => {
    const currentTime = moment
      .unix(value.get("unixTimestamp"))
      .tz(timezone[lang])
      .format("MM/DD \n HH:00")
    return currentTime
  })
  const max = Math.abs(
    parseInt(
      rtpData.reduce((pre, current) => (current > pre ? current : pre), 0)
    )
  )
  const min = Math.abs(
    parseInt(
      rtpData.reduce((pre, current) => (current < pre ? current : pre), 0)
    )
  )
  const rtpIconData =
    mediaType !== "pc" ? { onClick: rtpIconClick } : { onMouseEnter: mouseMove }
  return (
    <BarChart
      marginTop={marginTop}
      length={length}
      themeVariations={{}}
      mediaType={mediaType}
      column={column}
    >
      <div ref={$rtpIcon} className="rtpIcon" {...rtpIconData}>
        <ReactEcharts
          className="hoverIcon"
          option={iconOption({ rtpData: rtpData.toJS(), max, min })}
        />
      </div>
      {mediaType !== "pc" ? (
        <ReactModal isOpen={showState} onModalClose={() => setShowState(false)}>
          <div ref={$erpGraph} className={`erpGraph ${showState && "active"}`}>
            <div className="mask" onClick={rtpIconClick}></div>
            <div className="header">
              <div className="title">{gameName}</div>
              <IoMdClose onClick={rtpIconClick} className="close" />
            </div>
            <ReactEcharts
              option={option({
                rtpData: rtpData.toJS(),
                xAxisData: xAxisData.toJS(),
                max,
                min,
              })}
            />
          </div>
        </ReactModal>
      ) : (
        <div ref={$erpGraph} className="erpGraph">
          <div className="mask" onClick={rtpIconClick}></div>
          <div className="header">
            <div className="title">{gameName}</div>
            {mediaType !== "pc" && <IoMdClose className="close" />}
          </div>
          <ReactEcharts
            option={option({
              rtpData: rtpData.toJS(),
              xAxisData: xAxisData.toJS(),
              max,
              min,
            })}
          />
        </div>
      )}
    </BarChart>
  )
}

export default BarGraph
